export const APP_THEME_ID = "studio-white";

const LIGHT_CHROME_BACKGROUNDS = new Set([
  "#FFFFFF",
  "#F3F5F8",
  "#E5F7F8",
  "#F1FBFC",
  "#ECEFF4"
]);

const BEATS_STUDIO_WHITE = {
  id: "studio-white",
  name: "Studio White",
  chromeBg: "#FFFFFF",
  sidebarBg: "#FFFFFF",
  chromeText: "#1C2635",
  sidebarTextMuted: "#5A6472",
  tabActive: "#8F00FF",
  tabUnderline: "#8F00FF",
  tabText: "#1C2635",
  searchBg: "#F3F5F8",
  searchBorder: "#E0E3E8",
  searchText: "#1C2635",
  searchPlaceholder: "#5A6472",
  useColorLogo: true
};

const SURFACE_STUDIO_WHITE = {
  id: "studio-white",
  name: "Studio White",
  colors: {
    screenBg: "#F3F5F8",
    mainBg: "#F3F5F8",
    cardBg: "#FFFFFF",
    columnBg: "#ECEFF4",
    btnPrimary: "#8F00FF",
    borderActive: "#8F00FF",
    borderSubtle: "#E0E3E8",
    textHeader: "#1C2635",
    textSecondary: "#5A6472",
    statusDaily: "#003599",
    statusToday: "#00ABC1",
    statusLater: "#8F00FF",
    statusInProgress: "#00ABC1",
    statusComplete: "#5A6472",
    linkAccent: "#8F00FF"
  }
};

const THEME_CSS_VARS = [
  "--td-chrome-bg",
  "--td-chrome-text",
  "--td-nav-bg",
  "--td-nav-brand-bg",
  "--td-logo-brand-bg",
  "--td-nav-text",
  "--td-sidebar-bg",
  "--td-sidebar-text",
  "--td-tab-active",
  "--td-tab-underline",
  "--td-tab-text",
  "--td-sidebar-text-muted",
  "--td-search-bg",
  "--td-search-border",
  "--td-search-text",
  "--td-search-placeholder",
  "--td-screen-bg",
  "--td-main-bg",
  "--td-card-bg",
  "--td-column-bg",
  "--td-btn-primary",
  "--td-border-active",
  "--td-border-subtle",
  "--td-text-header",
  "--td-text-secondary",
  "--td-col-head",
  "--td-status-daily",
  "--td-status-today",
  "--td-status-later",
  "--td-status-in-progress",
  "--td-status-complete",
  "--td-link-accent"
];

function toCssVarName(key) {
  return `--td-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
}

function applyBeatsColors(root, beats) {
  const chromeBg = beats.chromeBg.toUpperCase();
  const sidebarBg = (beats.sidebarBg || beats.chromeBg).toUpperCase();
  const logoBrandBg = (beats.logoBrandBg || beats.chromeBg).toUpperCase();
  const isLightChrome = LIGHT_CHROME_BACKGROUNDS.has(chromeBg);

  root.style.setProperty("--td-chrome-bg", chromeBg);
  root.style.setProperty("--td-chrome-text", beats.chromeText);
  root.style.setProperty("--td-nav-bg", chromeBg);
  root.style.setProperty("--td-nav-brand-bg", logoBrandBg);
  root.style.setProperty("--td-logo-brand-bg", logoBrandBg);
  root.style.setProperty("--td-sidebar-bg", sidebarBg);
  root.style.setProperty("--td-nav-text", beats.chromeText);
  root.style.setProperty("--td-sidebar-text", beats.chromeText);
  root.style.setProperty("--td-tab-active", beats.tabActive);
  root.style.setProperty(
    "--td-tab-underline",
    beats.tabUnderline || beats.tabActive
  );
  root.style.setProperty("--td-tab-text", beats.tabText);
  root.style.setProperty(
    "--td-sidebar-text-muted",
    beats.sidebarTextMuted || beats.chromeText
  );

  if (beats.searchBg) {
    root.style.setProperty("--td-search-bg", beats.searchBg);
  } else {
    root.style.removeProperty("--td-search-bg");
  }

  root.style.setProperty(
    "--td-search-border",
    beats.searchBorder || (isLightChrome ? "#d4d4d4" : "transparent")
  );
  root.style.setProperty(
    "--td-search-text",
    beats.searchText || beats.chromeText
  );
  root.style.setProperty(
    "--td-search-placeholder",
    beats.searchPlaceholder || beats.sidebarTextMuted || beats.chromeText
  );

  root.setAttribute("data-app-chrome-light", isLightChrome ? "true" : "false");
  root.setAttribute(
    "data-app-logo-variant",
    beats.useColorLogo ? "color" : "white"
  );
  root.setAttribute(
    "data-app-logo-pill",
    beats.logoBrandPill ? "true" : "false"
  );
  root.setAttribute("data-app-theme", beats.id);
}

function applySurfaceColors(root, surface) {
  document.documentElement.style.setProperty(
    "--td-screen-bg",
    surface.colors.screenBg
  );

  Object.entries(surface.colors).forEach(([key, value]) => {
    root.style.setProperty(toCssVarName(key), value);
  });
}

export function applyAppTheme() {
  const root = document.body;

  root.classList.add("app-themed");
  applyBeatsColors(root, BEATS_STUDIO_WHITE);
  applySurfaceColors(root, SURFACE_STUDIO_WHITE);

  return { beats: BEATS_STUDIO_WHITE, surface: SURFACE_STUDIO_WHITE };
}

export function hasAuthSession() {
  return Boolean(localStorage.getItem("user-token"));
}

export function syncAppThemeForSession() {
  if (hasAuthSession()) {
    return applyAppTheme();
  }

  clearAppTheme();
  return null;
}

export function clearAppTheme() {
  const root = document.body;

  root.classList.remove("app-themed");
  root.removeAttribute("data-app-chrome-light");
  root.removeAttribute("data-app-logo-variant");
  root.removeAttribute("data-app-logo-pill");
  root.removeAttribute("data-app-theme");

  THEME_CSS_VARS.forEach(cssVar => {
    root.style.removeProperty(cssVar);
    document.documentElement.style.removeProperty(cssVar);
  });
}
